/*! report-downloader-mv3.js
 * Backend integration version
 * Ajustes realizados:
 * 1) La función global expuesta ahora es downloadPDF(reportId), no espera evento.
 * 2) Se setea VIEW_URL_DEFAULT usando el reportId recibido.
 * 3) Se eliminó la autoejecución al cargar (antes se hacía downloadPDF(new MouseEvent...)).
 * 4) Se corrigió la referencia a viewUrl -> ahora se usa VIEW_URL_DEFAULT siempre.
 * 5) Manejo seguro si no existe el span #btnReportDownload.
 */

let controlClick = false;

(async () => {
  'use strict';

  let idReport = undefined;
  let VIEW_URL_DEFAULT = undefined;

  const PIX = {
    renderScale: 2.0,
    nonWhiteChannelMax: 252,
    rowNonWhiteRatio: 0.008,
    minBandHeightPx: 6,
    verbose: true,
  };

  const ensurePdfJs = () => new Promise((resolve, reject) => {
    if (!Promise.allSettled) {
      Promise.allSettled = ps => Promise.all(ps.map(p => Promise.resolve(p)
        .then(v => ({ status: 'fulfilled', value: v }),
          r => ({ status: 'rejected', reason: r }))));
    }
    if (window.pdfjsLib) return resolve();
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js';
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js';
      resolve();
    };
    s.onerror = () => reject(new Error('No se pudo cargar pdf.js'));
    document.head.appendChild(s);
  });

  const ensurePdfLib = () => new Promise((resolve, reject) => {
    if (window.PDFLib) return resolve();
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/dist/pdf-lib.min.js';
    s.onload = resolve;
    s.onerror = () => reject(new Error('No se pudo cargar pdf-lib'));
    document.head.appendChild(s);
  });

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const absUrl = (u, base) => new URL(u, base).href;
  const toDownloadUrl = (viewUrl) => viewUrl.replace(/\/view(\?mode=pdf)?$/i, '/download');

  async function fetchArrayBuffer(url) {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status} al obtener: ${url}`);
    return await res.arrayBuffer();
  }
  async function fetchText(url) {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status} al obtener: ${url}`);
    return await res.text();
  }
  async function fetchWithType(url) {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status} al obtener: ${url}`);
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    return { ct, buf: await res.arrayBuffer() };
  }

  async function renderPageToCanvas(pdfjsDoc, pageIndex, scale = 1.5) {
    const page = await pdfjsDoc.getPage(pageIndex + 1);
    const vp = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(vp.width));
    canvas.height = Math.max(1, Math.floor(vp.height));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    return { canvas, viewport: vp };
  }

  async function getFinalImageBytesFromView(viewUrl) {
    const html = await fetchText(viewUrl);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    let img = doc.querySelector('.footer_image img, .footer-image img');
    if (!img) {
      const imgs = Array.from(doc.querySelectorAll('img[src]'));
      img = imgs[imgs.length - 1] || null;
    }
    if (!img) throw new Error('No se encontró imagen en la vista HTML.');

    const imgUrl = absUrl(img.getAttribute('src'), viewUrl);
    const { ct, buf } = await fetchWithType(imgUrl);

    if (/png|jpeg|jpg/.test(ct)) {
      return { bytes: buf, mime: ct };
    }

    const blob = new Blob([buf], { type: ct || 'application/octet-stream' });
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob);
    });
    const pngDataUrl = await new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0);
        try { resolve(canvas.toDataURL('image/png')); } catch (e) { reject(e); }
      };
      image.onerror = reject;
      image.src = dataUrl;
    });
    const pngResp = await fetch(pngDataUrl);
    const pngBuf = await pngResp.arrayBuffer();
    return { bytes: pngBuf, mime: 'image/png' };
  }

  async function processPdfRemoveLastAndCoverPenult(bytes) {
    await ensurePdfJs();
    await ensurePdfLib();
    const { PDFDocument, rgb } = PDFLib;

    const editDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
    let pageCount = editDoc.getPageCount();
    if (pageCount === 0) throw new Error('PDF sin páginas');
    if (pageCount === 1) {
      editDoc.removePage(0);
      return await editDoc.save();
    }

    const pdfjsDoc = await pdfjsLib.getDocument({ data: bytes }).promise;
    const penIndex = pageCount - 2;
    const { canvas: penCanvas } = await renderPageToCanvas(pdfjsDoc, penIndex, PIX.renderScale);
    const W = penCanvas.width, H = penCanvas.height;
    const ctx = penCanvas.getContext('2d');
    const data = ctx.getImageData(0, 0, W, H);

    let band = 0;
    for (let y = H - 1; y >= 0; y--) {
      let nonWhite = 0;
      const base = y * W * 4;
      for (let x = 0; x < W; x++) {
        const i = base + x * 4;
        const R = data.data[i], G = data.data[i + 1], B = data.data[i + 2], A = data.data[i + 3];
        if (A > 0 && (R < PIX.nonWhiteChannelMax || G < PIX.nonWhiteChannelMax || B < PIX.nonWhiteChannelMax)) nonWhite++;
      }
      const ratio = nonWhite / W;
      if (ratio >= PIX.rowNonWhiteRatio) band++;
      else break;
    }
    if (band < PIX.minBandHeightPx) band = 0;

    const coverPx = Math.min(band || Math.floor(H * 0.30), Math.floor(H * 0.30));
    const coverRatio = coverPx / H;

    const penPage = editDoc.getPage(penIndex);
    const coverPt = coverRatio * penPage.getHeight();
    if (coverPt > 0) {
      penPage.drawRectangle({ x: 0, y: 0, width: penPage.getWidth(), height: coverPt, color: rgb(1, 1, 1) });
      if (PIX.verbose) console.warn(`[penult] Cubierto inferior: ${(coverRatio * 100).toFixed(1)}%`);
    }

    editDoc.removePage(pageCount - 1);
    return await editDoc.save();
  }

  async function buildSingleImagePdfFromViewWithSize(viewUrl, targetWpt, targetHpt) {
    await ensurePdfLib();
    const { PDFDocument } = PDFLib;
    const { bytes: imgBytes, mime } = await getFinalImageBytesFromView(viewUrl);

    const dataUrl = await (async () => {
      const blob = new Blob([imgBytes], { type: mime || 'application/octet-stream' });
      return await new Promise((res, rej) => {
        const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob);
      });
    })();
    const { pxW, pxH } = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve({ pxW: im.naturalWidth || im.width, pxH: im.naturalHeight || im.height });
      im.onerror = reject;
      im.src = dataUrl;
    });

    const doc = await PDFDocument.create();
    const page = doc.addPage([targetWpt, targetHpt]);

    let embedded;
    if (/png/.test(mime)) embedded = await doc.embedPng(imgBytes);
    else if (/jpe?g/.test(mime)) embedded = await doc.embedJpg(imgBytes);
    else {
      const resp = await fetch(dataUrl); const buf = await resp.arrayBuffer();
      embedded = await doc.embedPng(buf);
    }

    const dpi = 180;
    const pxToPt = 72 / dpi;
    const imgWpt = pxW * pxToPt;
    const imgHpt = pxH * pxToPt;

    const margin = 24;
    const maxW = page.getWidth() - 2 * margin;
    const maxH = page.getHeight() - 2 * margin;
    const scale = Math.min(maxW / imgWpt, maxH / imgHpt);
    const drawW = imgWpt * scale;
    const drawH = imgHpt * scale;
    const x = (page.getWidth() - drawW) / 2;
    const y = (page.getHeight() - drawH) / 2;

    page.drawImage(embedded, { x, y, width: drawW, height: drawH });
    return await doc.save();
  }

  async function mergeCleanWithImagePdf(cleanBytes, imagePdfBytes) {
    await ensurePdfLib();
    const { PDFDocument } = PDFLib;
    const main = await PDFDocument.load(cleanBytes);
    const imgDoc = await PDFDocument.load(imagePdfBytes);
    const pages = await main.copyPages(imgDoc, imgDoc.getPageIndices());
    pages.forEach(p => main.addPage(p));
    const out = await main.save();
    const span = document.getElementById('link_download_pdf_1');
    const span_2 = document.getElementById('link_download_pdf_2')
    
    if (span) span.innerText = 'Descargar';
    if (span_2) span_2.innerText = 'DESCARGAR INFORME';

    controlClick = false;
    return new Blob([out], { type: 'application/pdf' });
  }

  async function downloadPDF(reportId) {
    if (controlClick) return;
    controlClick = true;
    idReport = reportId;
    VIEW_URL_DEFAULT = `https://vision-anterior.dav.ac/public/reports/${idReport}/view?mode=pdf`;
    const span = document.getElementById('link_download_pdf_1');
    const span_2 = document.getElementById('link_download_pdf_2')

    if (span) span.innerText = 'Descargando ...';
    if (span_2) span_2.innerText = 'DESCARGANDO ...';


    try {
      const downloadUrl = toDownloadUrl(VIEW_URL_DEFAULT);
      const originalBytes = await fetchArrayBuffer(downloadUrl);
      const cleanBytes = await processPdfRemoveLastAndCoverPenult(originalBytes);
      const { PDFDocument } = PDFLib;
      const cleanDocForSize = await PDFDocument.load(cleanBytes);
      const firstPage = cleanDocForSize.getPage(0);
      const targetWpt = firstPage.getWidth();
      const targetHpt = firstPage.getHeight();
      const imagePdfBytes = await buildSingleImagePdfFromViewWithSize(VIEW_URL_DEFAULT, targetWpt, targetHpt);
      const finalBlob = await mergeCleanWithImagePdf(cleanBytes, imagePdfBytes);
      downloadBlob(finalBlob, `${idReport}.pdf`);
    } catch (err) {
      console.error('Error generando el PDF final:', err);
      controlClick = false;
    }
  }

  window.downloadPDF = downloadPDF;
})();